import { IAttrValue, OWDataType } from "@multiweb/types/contracts";
import { Event } from "Global/Base/Events";
import { IItemRightsCollection } from "Global/Base/Rights/RightTypes";
import { DataConverter } from "../../../../Global/Base/Converters/DataConverter";
import { IAttrStringValue } from "../../../../Global/Base/DataTypes/IAttrValue";
import { DeferredStateful, PromiseUtils } from "../../../../Global/Base/Promises/PromiseUtils";
import { AttrValueReader } from "../../../../OW.Remoting/Remoting/AttrValueReader";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { Control } from "../../../UI.BaseControls/Control";
import { registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { UIManager } from "../../../UI/UI/UIManager";
import { FooterCell } from "../Components/FooterCell";
import { HeaderCell } from "../Components/HeaderCell";
import { DefaultEditTemplate } from "../Templates/DataTypeEditTemplates/DefaultEditTemplate";
import { EditItemTemplate } from "../Templates/EditItemTemplate";
import { FooterTemplate } from "../Templates/FooterTemplate";
import { HeaderTemplate } from "../Templates/HeaderTemplate";
import { ItemTemplate } from "../Templates/ItemTemplate";
import { IColumnDefinition, IColumnParameters } from "./ColumnFactory";

/**
     * Virtualni predek pro ruzne typy sloupcu
     * @author AndrlikP
     * @version 1.0
     * @created 25-I-2016 14:13:50
     */
export class Column extends Control {
    //#region Events
    public headerClickEvent: CustomEvent;
    //#endregion Events

    //#region Listeners
    public listenerHeaderClick: EventListener;
    public listenerDropDownClick: EventListener;
    public resizableDragEventListener: EventListener;

    public readonly columnWidthChange: Event<Column, number> = new Event<Column, number>();
    //#endregion Listeners

    private static _MIN_COLUMN_WIDTH: number | undefined = undefined;

    //#region Fields
    // ReSharper disable once InconsistentNaming
    /**
     * Minimální šířka sloupce v pixelech
     * (stejná hodnota je i ve stylu DataGrid.less - 3.2 Buňka seznamu)
     */
    public static get MIN_COLUMN_WIDTH(): number {
        if (Column._MIN_COLUMN_WIDTH === undefined) {
            // Nacteni minimalni sirky z CSS promenne
            Column._MIN_COLUMN_WIDTH = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--datagrid-cell-min-width"));
            if (isNaN(Column._MIN_COLUMN_WIDTH)) {
                Column._MIN_COLUMN_WIDTH = 24;
            }
        }

        return Column._MIN_COLUMN_WIDTH;
    }

    protected _width: number;

    private _attrName: string;
    public displayAttributeName?: string;
    protected _designMode: any;
    public isEditable: boolean = false;
    public attrNames: string[]; // seznam nazvu, z duvodu LOVAttribute, ktery obsahuje showattrs

    private _locKey: string;
    private _locParams: string[];
    private _trimValue: boolean = true;

    protected _isFrozen: boolean = false;
    public multiLine: boolean = false;
    public sortable: boolean = true;
    public headerCell: HeaderCell;
    public footerCell: FooterCell;
    public displayFormat: string;
    public dataType: OWDataType | string;
    public fullDataType: OWDataType | string;
    /** Určuje, zda se jedná o dynamický sloupec (např. z aplikační logiky) */
    public isDynamic: boolean = false;

    private _order: number;
    private _hideColumnMenu = false;

    private _columnGrow: boolean = false;
    private _widthBeforeGrow: number = 0;

    private _columnNameDefer: DeferredStateful<string>;
    private _columnTitleDefer: DeferredStateful<string>;
    private _interactive: boolean;
    public allowGrouping: boolean;
    public canFilterNull: boolean;
    public canFilterCompare: boolean;
    public canFilterEqual: boolean;
    public canFilterLike: boolean;
    public canSort: boolean;

    protected _additionalParameters: IColumnParameters;

    protected _itemTemplate: ItemTemplate;
    private _originalRightsDefinition: IItemRightsCollection;

    public get width(): number {
        return this._width;
    }
    public set width(value: number) {
        if (value < Column.MIN_COLUMN_WIDTH) {
            value = Column.MIN_COLUMN_WIDTH;
        }
        this._columnGrow = false;
        this._width = value;
        this.columnWidthChange.trigger(this, value);
    }

    public get isFrozen(): boolean {
        return this._isFrozen;
    }

    public set isFrozen(value: boolean) {
        this._isFrozen = value;
    }

    /**
     * Nastavení parametru pro zabraní celé šířky sloupce. Pokud je true, sloupec se bude roztahovat
     * @param value
     */
    public set columnGrow(value: boolean) {
        if (this._columnGrow != value) {
            this._columnGrow = value;
            if (value) {
                this._widthBeforeGrow = this._width;
                this._width = 0;
            } else {
                this._width = this._widthBeforeGrow;
            }
        }
    }

    public get columnGrow(): boolean {
        return this._columnGrow;
    }

    public get itemTemplate(): ItemTemplate {
        return this._itemTemplate;
    }
    public set itemTemplate(v: ItemTemplate) {
        this._itemTemplate = v;
    }

    public get columnNamePromise(): Promise<string> {
        return this._columnNameDefer ? this._columnNameDefer.promise : Promise.resolve(null);
    }

    public get columnTitlePromise(): Promise<string> {
        return this._columnTitleDefer ? this._columnTitleDefer.promise : Promise.resolve(null);
    }

    /** Příznak, jestli se májí ořezávat načítaná data atributu. Výchozí true */
    public get trimValue(): boolean {
        return this._trimValue;
    }

    public headerTemplate: HeaderTemplate;
    public footerTemplate: FooterTemplate;
    public editItemTemplate: EditItemTemplate;
    //#endregion Fields

    //#region Properties

    /** Definice práv pro element tomto sloupci. */
    public get originalRightsDefinition(): IItemRightsCollection {
        return this._originalRightsDefinition;
    }
    /**
     * Vrátí lokalizovany název sloupce
     */
    public get columnName(): string {
        return this._columnNameDefer && this._columnNameDefer.promiseValue || "";
    }

    /**
     * Lokalizacni klic popisku v hlavicce
     */
    public get locKey(): string {
        return this._locKey;
    }
    public set locKey(v: string) {
        this._locKey = v;
        this._updateColumnName();
    }

    /**
     * Parametry lokalizovaného názvu sloupce
     */
    public get locParams(): string[] {
        return this._locParams;
    }
    public set locParams(value: string[]) {
        this._locParams = value;
        this._updateColumnName();
    }

    /**
     * Název atributu sloupce
     */
    public get attrName(): string {
        return this._attrName;
    }

    public set attrName(value: string) {
        this._attrName = value;

        if (!this.attrNames) {
            this.attrNames = [value];
        }
    }

    /** Názvy atributů, které je potřeba dotáhnout ze serveru. */
    public get dataSourceAttrNames(): string[] {
        return this.attrNames;
    }

    /** Název atributu sloupce pro řazení - dle hodnoty */
    public get orderAttrName(): string {
        return this.displayAttributeName ? `${this.attrName}.${this.displayAttributeName}` : this.attrName;
    }

    /** Název atributu sloupce i s případnou vazbou */
    public get fullAttrName(): string {
        return this.attrNames[0];
    }

    /** Název filtrovacího atributu */
    public get filterAttrName(): string {
        if (this.displayAttributeName) {
            return [this.attrName, this.displayAttributeName].join(".");
        }
        return this.fullAttrName;
    }

    /** Zda se jedná o typ sloupce bez atributu */
    public get withoutAttribute(): boolean {
        return this._attrName == null;
    }

    /**Vrací jestli se má skrýt sloupcové menu v hlavičce */
    public get hideColumnMenu(): boolean {
        return this._hideColumnMenu;
    }

    /**Nastaví jestli se má skrýt sloupcové menu v hlavičce */
    public set hideColumnMenu(val: boolean) {
        this._hideColumnMenu = val;
    }

    public get columnId(): string {
        return this.getAttribute("data-id");
    }

    public get interactive(): boolean {
        return this._interactive;
    }

    public set interactive(v: boolean) {
        this._interactive = v;

        if (this.headerCell) {
            this.headerCell.interactive = v;
        }

        if (this.footerCell) {
            this.footerCell.interactive = v;
        }
    }

    /**
    * Reprezentuje poradi sloupce v seznamu
    */
    public get order(): number {
        return this._order;
    }

    /**
    * Reprezentuje poradi sloupce v seznamu
    */
    public set order(v: number) {
        this._order = v;
    }

    public set isFiltered(v: boolean) {
        this.headerTemplate.classList.toggle("Filtering", v);
    }

    public get isFiltered(): boolean {
        return this.headerTemplate.classList.contains("Filtering");
    }

    public get additionalParameters(): IColumnParameters {
        return this._additionalParameters;
    }

    protected static override get observedAttributes(): string[] {
        return ["data-attrname", "id", "data-lockey", "data-locparams", "data-displayformat", "data-width", "data-iseditable", "data-culture", "data-visible", "data-multiline", "data-trimvalue"];
    }
    //#endregion

    //#region Methods

    public async formatValue(data: IAttrValue | IAttrValue[]): Promise<string> {
        if (data instanceof Array) {
            if (data.length === 0) {
                return "";
            }
            data = data[0];
        }
        const val = AttrValueReader.read(data, [IAttrStringValue]);

        if (val == null) {
            return "";
        }

        return val.toString();
    }

    /**
     * Zinicializuje data sloupce
     * @param definition - definice sloupce
     * @param params - dodatečné parametry
     */
    public initialize(definition: IColumnDefinition, _params?: IColumnParameters): void {
        // Název atributu naplníme ihned, šahá se na něj během vytváření sloupců při plnění dataSource (ještě nemusel proběhnout attributeChangedCallback)
        this.attrName = definition.attrName;

        // Tyto atributy nechceme vytvářet, buď jsou vytvořeny na potomcích zvlášť bez prefixu nebo nemají být vůbec obsaženy
        const ignoredAttributes = ["id", "compactarea", "compactareaorder", "enumname", "locparams"];

        Object.entries(definition).forEach(([key, value]) => { // Vytvoří atributy, na jejichž změny můžeme reagovat
            if (value != null && ignoredAttributes.indexOf(key.toLowerCase()) < 0) {
                this.setAttribute("data-" + key.toLowerCase(), value instanceof Array ? value.join(";") : value);
            }
        });

        if (definition.locParams != null) {
            this.setAttribute("data-locparams", JSON.stringify(definition.locParams));
        }

        //this.id = (definition.id ? definition.id : UIManager.instance.generateElementID()) + "_col";

        this.setAttribute("data-id", (definition.id ? definition.id : UIManager.instance.generateElementID()) + "_col");
        this._originalRightsDefinition = definition.originalRightsDefinition;
    }

    public onColumnChanged(): void {
        // Prázdná metoda
    }

    public disableSort(): void {
        if (this.headerTemplate) {
            this.headerTemplate.disableSort();
        }
    }

    //#endregion Methods

    //#region dispose
    /**
     * Destruktor
     */
    public override dispose(): void {
        super.dispose();

        this.headerTemplate?.dispose();
        this.itemTemplate?.dispose();
        this.editItemTemplate?.dispose();
    }
    //#endregion dispose

    //#region HTMLElement
    protected override _constructor(): void {
        super._constructor();

        this.headerTemplate = new HeaderTemplate();
        this.itemTemplate = new ItemTemplate();
        this.editItemTemplate = new DefaultEditTemplate();
        this._setOnLanguageChangedListener();
        this.disposeOnDetach = false;
    }

    // You can also define the other lifecycle methods.
    protected override connectedCallback(): void {
        super.connectedCallback();
    }

    protected override disconnectedCallback(): void {
        super.disconnectedCallback();
    }

    /**
     * Reakce na změny attributu
     * @param attrName - název změněného atributu
     * @param oldVal - původní hodnota
     * @param newVal - nová hodnota
     */
    protected override attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        super.attributeChangedCallback(attrName, oldVal, newVal);

        switch (attrName) {
            case "data-attrname":
                this.attrName = newVal;
                break;
            case "data-lockey":
                this.locKey = newVal;
                break;
            case "data-locparams":
                this.locParams = JSON.parse(newVal);
                break;
            case "data-displayformat":
                this.displayFormat = newVal;
                break;
            case "data-width":
                this.width = +newVal;
                break;
            case "data-iseditable":
                this.isEditable = DataConverter.toBoolean(newVal, false);
                break;
            case "data-culture":
                this._culture = newVal;
                break;
            case "data-visible":
                this.visible = DataConverter.toBoolean(newVal, true);
                break;
            case "data-multiline":
                this.multiLine = DataConverter.toBoolean(newVal, false);
                break;
            case "data-trimvalue":
                this._trimValue = DataConverter.toBoolean(newVal, true);
                break;
        }
    }

    //#endregion HTMLElement

    private _setOnLanguageChangedListener(): void {
        this._eventsController.add(Localization.languageChanged, () => this._onLocKeyChanged());
    }

    private _onLocKeyChanged(): void {
        this._updateColumnName();
        this._updateColumnTitle();
    }

    private _updateColumnName(): void {
        const statePromise = this._columnNameDefer = PromiseUtils.deferStateful<string>();
        Localization.getTextPromiseWithParams(this.locKey, this.locParams)
            .then(columnName => statePromise.resolve(columnName));
    }

    private _updateColumnTitle(): void {
        const statePromise = this._columnTitleDefer = PromiseUtils.deferStateful<string>();
        Localization.getDescriptionPromiseWithParams(this.locKey, this.locParams)
            .then(columnTitle => statePromise.resolve(columnTitle));
    }
} //end Column

registerCustomElement("tsc-column", Column);
